import React, { useState } from 'react';
import { Link } from 'react-router';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import AIClassificationRuns from './AIClassificationRuns';
import { TEXT_COLOR } from '../../theme';
import { matchAgainstLabels } from '../../utils/alternativeLabels';

const CARD_SX = {
    bgcolor: '#fff',
    border: '1px solid #E1E4E8',
    borderRadius: '14px',
    boxShadow: '0 1px 2px rgba(15,17,35,0.04)',
    p: 3,
};

const MATCH_CHIPS = {
    label: { text: 'ai_matches_label', color: 'success' },
    alternative: { text: 'ai_matches_alternative', color: 'warning' },
    none: { text: 'ai_no_match', color: 'default' },
};

// Cómo queda la corrida que se mira frente a lo etiquetado en pantalla, sin
// guardar: así el staff ve el efecto de agregar una alternativa al instante.
function MatchChip({ tr, classification, nivelLabel, subnivelLabel, alternativeLabels }) {
    const match = matchAgainstLabels(classification, nivelLabel, subnivelLabel, alternativeLabels);
    if (!match) return null;
    const { text, color } = MATCH_CHIPS[match];
    return <Chip label={tr(text)} color={color} variant="outlined" size="small" sx={{ ml: 1 }} />;
}

function Stats({ tr, classification }) {
    const { input_tokens, output_tokens, duration_seconds } = classification;
    return (
        <Typography sx={{ fontSize: '12px', color: 'text.secondary', mt: 1.5 }}>
            {`${input_tokens} + ${output_tokens} ${tr('ai_tokens')} · ${Number(duration_seconds).toFixed(1)}s · `}
            {/* El spec con el que corrió esta clasificación lleva al panel, que
                es donde se cambia el default. */}
            <Typography
                component={Link}
                to="/ai-admin"
                sx={{ fontSize: '12px', color: 'inherit', textDecoration: 'underline' }}
            >
                {classification.spec_name}
            </Typography>
        </Typography>
    );
}

// El servicio tiene cientos de specs, así que el selector es un Autocomplete y
// no un Select: sin buscar, elegir uno es imposible.
function SpecPicker({ tr, specs, value, onChange, disabled }) {
    return (
        <Autocomplete
            size="small"
            options={specs.map((s) => s.name)}
            value={value || null}
            onChange={(_, next) => onChange(next ?? '')}
            disabled={disabled}
            sx={{ minWidth: 260, flex: '1 1 260px' }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={tr('ai_run_spec')}
                    placeholder={tr('ai_spec_placeholder')}
                    helperText={tr('ai_spec_help')}
                />
            )}
        />
    );
}

export default function AIClassificationCard({
    tr, classification, classifications = [], classify, starting, error, errorDetail,
    timedOut, pending, specs = [], specsError = '',
    selectedSpec, onSelectSpec,
    nivelLabel = '', subnivelLabel = '', alternativeLabels = [],
}) {
    // Qué corrida se está mirando en el detalle. Se guarda junto al estado de la
    // más reciente al elegirla: cuando ese cambia la selección caduca sola y el
    // detalle vuelve a la última, sin necesitar un efecto.
    const [selection, setSelection] = useState(null);
    const latest = classifications[0];
    // El id solo no alcanza: la fila pendiente se prepende al lanzarla, así que
    // ya es la más reciente antes de tener resultado. Con el status adentro, una
    // selección hecha durante la espera también caduca cuando esa corrida
    // termina, que es cuando hay algo nuevo para mostrar.
    const latestKey = latest ? `${latest.id}:${latest.status}` : null;
    const shownRun = (
        selection?.latestKey === latestKey
            ? classifications.find((c) => c.id === selection.runId)
            : null
    ) ?? classification;

    const running = pending || starting;

    const selectRun = (run) => {
        setSelection({ runId: run.id, latestKey });
        // El spec de la corrida que mirás es el que vas a querer repetir, salvo
        // mientras corre una: ahí el picker está deshabilitado y el cambio no se
        // podría deshacer hasta que termine.
        if (!running) onSelectSpec(run.spec_name);
    };

    return (
        <Box sx={CARD_SX} data-testid="ai-classification-card">
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700, color: TEXT_COLOR }}>
                    {tr('ai_classification')}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* La card sólo la ve el staff, así que el acceso al panel
                        va acá: sirve también antes de la primera corrida, cuando
                        todavía no hay un spec_name que mostrar abajo. */}
                    <Tooltip title={tr('ai_admin_title')}>
                        <IconButton component={Link} to="/ai-admin" size="small" aria-label={tr('ai_admin_title')}>
                            <TuneOutlinedIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={running ? <CircularProgress size={16} /> : <AutoAwesomeOutlinedIcon />}
                        onClick={() => classify(selectedSpec)}
                        disabled={running || !selectedSpec}
                    >
                        {classification ? tr('ai_reclassify') : tr('ai_classify')}
                    </Button>
                </Box>
            </Box>

            {/* El spec no se configura en el servidor: se elige acá, por
                corrida, que es lo que permite comparar modelos. */}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1, flexWrap: 'wrap' }}>
                <SpecPicker
                    tr={tr}
                    specs={specs}
                    value={selectedSpec}
                    onChange={onSelectSpec}
                    disabled={running || Boolean(specsError)}
                />
            </Box>

            {specsError && (
                <Alert severity="error" sx={{ mb: 2 }}>{tr('ai_specs_unavailable')}</Alert>
            )}

            {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {errorDetail || tr(error)}
                </Alert>
            )}

            {timedOut && <Alert severity="warning" sx={{ mb: 2 }}>{tr('ai_timeout')}</Alert>}

            {pending && (
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                    {tr('ai_classifying')}
                </Typography>
            )}

            {!pending && !classification && !error && (
                <Typography sx={{ fontSize: '14px', color: 'text.secondary' }}>
                    {tr('ai_no_classification')}
                </Typography>
            )}

            {/* El error que se muestra es el de la corrida que se está mirando:
                si falló la última pero el detalle es el de una vieja que salió
                bien, el error de la otra no tiene que aparecer. */}
            {shownRun?.status === 'E' && (
                <Alert severity="error">
                    {tr('ai_error')}
                    {shownRun.error ? `: ${shownRun.error}` : ''}
                </Alert>
            )}

            {/* El detalle es el de la corrida que se esté mirando, que por
                defecto es la última pero cambia al elegir otra en la tabla.
                Sigue disponible mientras corre otra clasificación: comparar
                specs es justamente esperar una nueva mirando la anterior. */}
            {shownRun?.status === 'O' && (
                <Box>
                    {shownRun.is_tied ? (
                        <Alert severity="warning">{tr('ai_tied')}</Alert>
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Chip
                                label={`${shownRun.level}${shownRun.sublevel}`}
                                color="primary"
                                sx={{ fontWeight: 700 }}
                            />
                            <MatchChip
                                tr={tr}
                                classification={shownRun}
                                nivelLabel={nivelLabel}
                                subnivelLabel={subnivelLabel}
                                alternativeLabels={alternativeLabels}
                            />
                        </Box>
                    )}

                    {shownRun.reason && (
                        <Box sx={{ mt: 2 }}>
                            <Typography sx={{
                                fontSize: '11px', fontWeight: 600, textTransform: 'uppercase',
                                letterSpacing: '0.07em', color: TEXT_COLOR, mb: 0.75,
                            }}>
                                {tr('ai_reason')}
                            </Typography>
                            <Typography sx={{ fontSize: '14px', color: 'text.secondary', whiteSpace: 'pre-wrap' }}>
                                {shownRun.reason}
                            </Typography>
                        </Box>
                    )}

                    <Stats tr={tr} classification={shownRun} />
                </Box>
            )}

            <AIClassificationRuns
                tr={tr}
                runs={classifications}
                shownRunId={shownRun?.id}
                onSelectRun={selectRun}
            />
        </Box>
    );
}
